import { ClientTaxDocumentation } from "@taxbit/react-sdk";
import countryList from "react-select-country-list";
import { AddressRes } from "src/store/features/api/generated";
import { Details } from "src/store/features/user.slice";

// Seeds the W-9/W-8 questionnaire from our KYC record. Factual fields only — never the TIN or
// the Part II certification, which the user has to attest to themselves.

// We store country as a label ("United States of America (USA)"); TaxBit wants ISO alpha-2.
const countries = countryList();

const VALID_ALPHA2 = new Set(countries.getValues());

const COUNTRY_ALIASES: Record<string, string> = {
  "united states of america": "US",
  usa: "US",
  uk: "GB",
  "great britain": "GB",
};

const toIsoCountry = (country?: string): string | undefined => {
  const raw = country?.trim();
  if (!raw) return undefined;

  if (/^[A-Za-z]{2}$/.test(raw) && VALID_ALPHA2.has(raw.toUpperCase())) return raw.toUpperCase();

  const withoutParenthetical = raw.replace(/\s*\([^)]*\)\s*$/, "").trim();
  return (
    countries.getValue(withoutParenthetical) ||
    COUNTRY_ALIASES[withoutParenthetical.toLowerCase()] ||
    undefined
  );
};

// TaxBit's date inputs are locale-ordered, so only pass an unambiguous ISO date.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const toIsoDate = (dob?: string): string | undefined =>
  dob && ISO_DATE.test(dob.trim()) ? dob.trim() : undefined;

const clean = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

type PrefillAddress = NonNullable<NonNullable<ClientTaxDocumentation["accountHolder"]>["address"]>;

// Country is a fixed dropdown, so an unresolved value is left unset here rather than passed
// through as free text.
const buildAddress = (address?: AddressRes): PrefillAddress | undefined => {
  const built: PrefillAddress = {
    country: toIsoCountry(address?.country),
    firstLine: clean(address?.streetAddress),
    city: clean(address?.city),
    stateOrProvince: clean(address?.state),
    postalCode: clean(address?.zip),
  };

  return Object.values(built).some((value) => value !== undefined) ? built : undefined;
};

const buildName = (details?: Details): string | undefined => {
  const first = clean(details?.firstName);
  const last = clean(details?.lastName);
  if (first && last) return `${first} ${last}`;
  return clean(details?.name);
};

export const buildTaxDocumentationPrefill = (
  details?: Details,
  address?: AddressRes,
): ClientTaxDocumentation | undefined => {
  const accountHolder = {
    name: buildName(details),
    dateOfBirth: toIsoDate(details?.dob),
    emailAddress: clean(details?.email),
    phoneNumber: clean(details?.phone),
    address: buildAddress(address),
    isIndividual: true,
  };

  const hasAnyValue = Object.entries(accountHolder).some(
    ([key, value]) => key !== "isIndividual" && value !== undefined,
  );
  return hasAnyValue ? { accountHolder } : undefined;
};
